import { Layout, Spin } from 'antd';
import 'primeflex/primeflex.css';
import { useMemo, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import ProtectedRoute from './routes/ProtectedRoute';
import { getRoutes } from './routes/Routes';
import './styles/index.scss';
import LoadingOverlayWrapper from 'react-loading-overlay-ts';
import PublicLayout from './components/layout/Public';

const App = () => {
  const { token: authToken } = useSelector((state) => state.authSlice);
  const { loading } = useSelector((state) => state.loadingSlice);
  const location = useLocation();
  const { publicRoutes, protectedRoutes } = useMemo(() => {
    const allRoutes = Object.values(getRoutes());
    return {
      publicRoutes: allRoutes.filter((r) => r.public || r.global),
      protectedRoutes: allRoutes.filter((r) => !r.public || (!r.public && r.global)),
    };
  }, []);

  const isKiosk = location.pathname.startsWith(getRoutes().kiosk.route());
  const isOverlayActive = loading && !isKiosk;

  return (
    <LoadingOverlayWrapper
      spinner={isOverlayActive ? <Spin fullscreen size="large" /> : false}
      active={isOverlayActive}
      styles={{
        overlay: (base) => ({
          ...base,
          background: 'rgba(255, 255, 255, 0.5)',
          height: '100vh',
          zIndex: 9999,
        }),
      }}
    >
      <Layout style={{ height: '100dvh', overflow: 'auto' }}>
        <Layout.Content>
          <Suspense
            fallback={
              <Spin fullscreen size="large" />
            }
          >
            <Routes>
              {/* Public Routes */}
              {!authToken &&
                publicRoutes?.map((route) => (
                  <Route
                    key={route.name}
                    path={route.path}
                    element={
                      route.standalone ? (
                        <route.element />
                      ) : (
                        <PublicLayout global={route?.global ?? false}>
                          <route.element />
                        </PublicLayout>
                      )
                    }
                  />
                ))}

              {/* Protected Routes */}
              {authToken &&
                protectedRoutes?.map((route) => (
                  <Route
                    key={route.name}
                    path={route.path}
                    element={
                      <ProtectedRoute
                        allowedRoles={route.allowedRoles}
                        standalone={route.standalone}
                      >
                        <route.element />
                      </ProtectedRoute>
                    }
                  />
                ))}

              {/* Fallback - an unmatched path while signed out is almost always a
                  protected route (e.g. /kiosk) that just isn't registered above;
                  send it to login rather than the public marketing home. */}
              <Route
                path="*"
                element={<Navigate to={authToken ? '/' : getRoutes().login.route()} replace />}
              />
            </Routes>
          </Suspense>
        </Layout.Content>
      </Layout>
    </LoadingOverlayWrapper>
  );
};

export default App;
